import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Calendar, ChevronDown, Download } from 'lucide-react';
import { Legend, Line } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Shipment Timeline - weekly/monthly overview of dock dates
function ShipmentTimeline() {
  const [data, setData] = useState(null);
  const [view, setView] = useState('weekly');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/planning/shipment-overview?category=City%20Bus`);
        setData(res.data);
      } catch { }
    })();
  }, []);

  if (!data) return null;

  const periods = view === 'weekly' ? data.weekly : data.monthly;
  const now = new Date();
  const currentWeek = `${now.getFullYear()}-W${String(Math.ceil(((now - new Date(now.getFullYear(), 0, 1)) / 86400000 + new Date(now.getFullYear(), 0, 1).getDay() + 1) / 7)).padStart(2, '0')}`;
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const cutoff = view === 'weekly' ? currentWeek : currentMonth;

  const totalSets = periods.reduce((s, p) => s + p.total_sets, 0);
  const pastSets = periods.filter(p => p.period <= cutoff).reduce((s, p) => s + p.total_sets, 0);
  const futureSets = totalSets - pastSets;
  const unscheduled = data.unscheduled || [];
  const totalMissing = data.total_missing || 0;

  // Color map for projects
  const projectColors = {};
  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-teal-500', 'bg-pink-500', 'bg-indigo-500'];
  let colorIdx = 0;
  periods.forEach(p => p.projects.forEach(pr => {
    if (!projectColors[pr.project]) { projectColors[pr.project] = colors[colorIdx % colors.length]; colorIdx++; }
  }));

  const maxSets = Math.max(...periods.map(p => p.total_sets), 1);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="shipment-timeline">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-bold text-slate-800">Dock Date Schedule</span>
          <span className="text-xs text-slate-400">{totalSets} scheduled ({pastSets} past, {futureSets} upcoming){totalMissing > 0 && <span className="text-amber-500 font-semibold"> | {totalMissing} unscheduled</span>}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            try {
              const res = await axios.get(`${API}/api/planning/shipment-overview/export?category=City%20Bus&view=${view}`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url;
              a.download = `Line_Entry_Schedule_${new Date().toISOString().slice(0,10)}.xlsx`;
              a.click(); window.URL.revokeObjectURL(url);
            } catch { toast.error('Export failed'); }
          }}
            className="flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded-lg text-[10px] font-semibold hover:bg-emerald-700">
            <Download className="w-3 h-3" /> Export
          </button>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            {['weekly', 'monthly'].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 rounded-md text-[10px] font-semibold transition-all ${view === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                {v === 'weekly' ? 'Weekly' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Unscheduled warning */}
      {unscheduled.length > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[10px] font-bold text-amber-700">{totalMissing} sets without dock dates</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {unscheduled.map(u => (
              <span key={u.project_id} className="text-[9px] bg-white border border-amber-300 text-amber-700 px-2 py-0.5 rounded font-semibold">
                {u.project}: <span className="font-bold">{u.missing_total}</span> of {u.ordered} missing
                {u.not_scheduled > 0 && <span className="text-amber-500"> ({u.not_scheduled} not created)</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-2 flex flex-wrap gap-2 border-b border-slate-50">
        {Object.entries(projectColors).map(([name, color]) => (
          <span key={name} className="flex items-center gap-1 text-[10px] text-slate-600">
            <span className={`w-2.5 h-2.5 rounded-sm ${color}`} />{name}
          </span>
        ))}
      </div>

      {/* Timeline bars */}
      <div className="px-4 py-3 space-y-1 max-h-[320px] overflow-y-auto">
        {periods.map(p => {
          const isPast = p.period < cutoff;
          const isCurrent = p.period === cutoff;
          const isExpanded = expanded === p.period;
          const label = view === 'weekly' ? p.period.replace(/^\d{4}-W/, 'W') : p.period;

          return (
            <div key={p.period}>
              <div className={`flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 rounded-lg px-1 transition-colors ${isCurrent ? 'bg-blue-50' : ''}`}
                onClick={() => setExpanded(isExpanded ? null : p.period)}>
                <span className={`text-[10px] font-mono w-14 text-right ${isCurrent ? 'font-bold text-blue-600' : isPast ? 'text-slate-400' : 'text-slate-600 font-semibold'}`}>
                  {label}
                </span>
                {/* Stacked bar */}
                <div className="flex-1 flex items-center h-5 rounded-md overflow-hidden bg-slate-50" style={{ maxWidth: '70%' }}>
                  {p.projects.map((pr, pi) => (
                    <div key={pi} className={`h-full ${projectColors[pr.project] || 'bg-slate-400'} ${isPast ? 'opacity-40' : ''}`}
                      style={{ width: `${(pr.count / maxSets) * 100}%`, minWidth: pr.count > 0 ? '8px' : '0' }}
                      title={`${pr.project}: ${pr.count} sets`} />
                  ))}
                </div>
                <span className={`text-[11px] font-bold min-w-[24px] text-right ${isCurrent ? 'text-blue-600' : isPast ? 'text-slate-400' : 'text-slate-700'}`}>
                  {p.total_sets}
                </span>
                <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>

              {isExpanded && (
                <div className="ml-16 mb-2 bg-slate-50 rounded-lg p-2 border border-slate-100">
                  {p.projects.map((pr, pi) => (
                    <div key={pi} className="mb-1.5 last:mb-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`w-2 h-2 rounded-sm ${projectColors[pr.project]}`} />
                        <span className="text-[10px] font-bold text-slate-700">{pr.project}</span>
                        <span className="text-[9px] text-slate-400">{pr.sr_number}</span>
                        <span className="text-[10px] font-semibold text-blue-600">{pr.count} sets</span>
                      </div>
                      <div className="ml-4 flex flex-wrap gap-1">
                        {pr.sets.map((s, si) => (
                          <span key={si} className={`text-[9px] px-1.5 py-0.5 rounded border ${
                            s.status === 'completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                            s.status === 'in_progress' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                            'bg-white border-slate-200 text-slate-600'
                          }`}>
                            Set {s.set_number} <span className="text-slate-400 ml-0.5">{s.date}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ShipmentTimeline;
